import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import certifiedTrainerService from '../../services/certifiedTrainer';
import { Course, PaymentCapabilities } from '../../types';
import { isFlagOn } from '../../utils/paymentFlags';
import { formatCourseStartDate } from '../../utils/courseDate';
import { stripHtml } from '../../utils/html';
import ConfirmModal from '../../components/common/ConfirmModal';
import { useToast } from '../../hooks/useToast';

export default function TeachingCourses() {
  const { t } = useTranslation('teaching');
  const toast = useToast();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState<string>('all');
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText: string;
    variant?: 'danger' | 'warning' | 'info';
  } | null>(null);
  const [paymentCapabilities, setPaymentCapabilities] = useState<PaymentCapabilities | null>(null);

  useEffect(() => {
    loadCourses();
    certifiedTrainerService.getPaymentCapabilities().then(setPaymentCapabilities).catch(() => {});
  }, []);

  async function loadCourses() {
    try {
      const data = await certifiedTrainerService.getCourses();
      setCourses(data);
    } catch (err: any) {
      setError(err.response?.data?.error || t('courses.failedToLoadCourses'));
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateCourse() {
    setCreating(true);
    try {
      const newCourse = await certifiedTrainerService.createCourse({
        title: t('courses.untitledCourse'),
        slug: 'untitled-course-' + Date.now(),
        description: '',
        status: 'draft',
      });
      navigate(`/my-teaching/courses/${newCourse._id}`);
    } catch (err: any) {
      toast.error(err.response?.data?.error || t('courses.failedToCreateCourse'));
      setCreating(false);
    }
  }

  function handleSubmitForReview(courseId: string) {
    setConfirmModal({
      isOpen: true,
      title: t('courses.submitForReviewTitle'),
      message: t('courses.submitForReviewMessage'),
      confirmText: t('courses.submitButton'),
      variant: 'warning',
      onConfirm: async () => {
        try {
          await certifiedTrainerService.submitCourseForReview(courseId);
          loadCourses();
        } catch (err: any) {
          toast.error(err.response?.data?.error || t('courses.failedToSubmitCourse'));
        }
        setConfirmModal(null);
      },
    });
  }

  function handlePublish(courseId: string) {
    setConfirmModal({
      isOpen: true,
      title: t('courses.publishCourseTitle'),
      message: t('courses.publishCourseMessage'),
      confirmText: t('courses.publishButton'),
      variant: 'info',
      onConfirm: async () => {
        try {
          await certifiedTrainerService.publishCourse(courseId);
          loadCourses();
        } catch (err: any) {
          toast.error(err.response?.data?.error || t('courses.failedToPublishCourse'));
        }
        setConfirmModal(null);
      },
    });
  }

  const filteredCourses = courses.filter((c) =>
    filter === 'all' ? true : c.status === filter
  );

  const filterLabels: Record<string, string> = {
    all: t('courses.filterAll'),
    draft: t('courses.filterDraft'),
    pending: t('courses.filterPending'),
    approved: t('courses.filterApproved'),
    rejected: t('courses.filterRejected'),
    published: t('courses.filterPublished'),
  };

  const statusCounts = {
    all: courses.length,
    draft: courses.filter((c) => c.status === 'draft').length,
    pending: courses.filter((c) => c.status === 'pending').length,
    approved: courses.filter((c) => c.status === 'approved').length,
    rejected: courses.filter((c) => c.status === 'rejected').length,
    published: courses.filter((c) => c.status === 'published').length,
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('courses.title')}</h1>
          <p className="text-gray-500 mt-1">{t('courses.subtitle')}</p>
        </div>
        <button
          onClick={handleCreateCourse}
          disabled={creating}
          className="btn-gold px-4 py-2"
        >
          {creating ? t('courses.creating') : t('courses.createNewCourse')}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-md mb-6">{error}</div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
        {(['all', 'draft', 'pending', 'approved', 'rejected', 'published'] as const).map(
          (status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 -mb-px whitespace-nowrap ${
                filter === status
                  ? 'border-b-2 border-gold text-gold font-medium'
                  : 'text-gray-500 hover:text-gray-600'
              }`}
            >
              {filterLabels[status]}
              <span className="ml-2 text-xs bg-gray-200 px-2 py-0.5 rounded-full text-gray-600">
                {statusCounts[status]}
              </span>
            </button>
          )
        )}
      </div>

      {filteredCourses.length === 0 ? (
        <div className="text-center py-12 glass-card rounded-lg">
          <p className="text-gray-500 mb-4">
            {filter === 'all'
              ? t('courses.noCoursesYet')
              : t('courses.noFilteredCourses', { filter: filterLabels[filter] })}
          </p>
          {filter === 'all' && (
            <button
              onClick={handleCreateCourse}
              disabled={creating}
              className="text-gold hover:underline"
            >
              {creating ? t('courses.creating') : t('courses.createFirstCourse')}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredCourses.map((course) => (
            <div
              key={course._id}
              className="glass-card rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex gap-6">
                {course.thumbnail ? (
                  <img
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-48 h-32 object-cover rounded-lg flex-shrink-0"
                  />
                ) : (
                  <div className="w-48 h-32 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-gray-500 text-sm">{t('courses.noThumbnail')}</span>
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {course.title}
                      </h3>
                      <span
                        className={`inline-block mt-1 px-2 py-1 rounded text-xs font-medium ${
                          course.status === 'published'
                            ? 'bg-green-100 text-green-800'
                            : course.status === 'approved'
                            ? 'bg-gold/20 text-gold'
                            : course.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : course.status === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {course.status}
                      </span>
                    </div>
                  </div>

                  <p className="text-gray-500 line-clamp-2 mb-3">{stripHtml(course.description)}</p>

                  {/* Rejection reason */}
                  {course.status === 'rejected' && course.rejectionReason && (
                    <div className="bg-red-50 p-3 rounded-md mb-3">
                      <p className="text-sm text-red-600">
                        <strong>{t('courses.rejectionReason')}</strong> {course.rejectionReason}
                      </p>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 text-xs mb-4">
                    <span className="px-2 py-1 bg-gray-100 rounded text-gray-600">{t('courses.lessons', { count: course.lessonCount || 0 })}</span>
                    {course.category && <span className="px-2 py-1 bg-gray-100 rounded text-gray-600">{course.category}</span>}
                    {course.duration && <span className="px-2 py-1 bg-gray-100 rounded text-gray-600">{course.duration}</span>}
                    <span className="px-2 py-1 bg-gray-100 rounded text-gray-600">{course.price || 0} {course.currency || 'USD'}</span>
                    {course.startDate && (
                      <span className="px-2 py-1 bg-blue-50 rounded text-blue-600">{t('courses.starts', { date: formatCourseStartDate(course) })}</span>
                    )}
                    {!isFlagOn(course.stripeEnabled) ? (
                      <span className="px-2 py-1 bg-red-50 rounded text-red-500">{t('courses.stripeOff')}</span>
                    ) : course.paymentProvider === 'custom' ? (
                      <span className="px-2 py-1 bg-amber-50 rounded text-amber-700">{t('courses.customStripe')}</span>
                    ) : paymentCapabilities?.customPaymentEnabled ? (
                      <span className="px-2 py-1 bg-purple-50 rounded text-purple-600">{t('courses.platformStripe')}</span>
                    ) : null}
                    {paymentCapabilities?.hasQrMethods && isFlagOn(course.qrPaymentEnabled) && (
                      <span className="px-2 py-1 bg-emerald-50 rounded text-emerald-700">{t('courses.qrPayment')}</span>
                    )}
                    {course.status === 'published' && (
                      <span className="px-2 py-1 bg-green-50 rounded text-green-600">{t('courses.members', { count: (course as any).activeStudents || 0 })}</span>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Link
                      to={`/my-teaching/courses/${course._id}`}
                      className="px-4 py-2 border border-gray-200 rounded-md text-gray-600 hover:bg-gray-100"
                    >
                      {t('courses.editCourse')}
                    </Link>

                    {course.status === 'draft' && (
                      <button
                        onClick={() => handleSubmitForReview(course._id)}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
                      >
                        {t('courses.submitForReview')}
                      </button>
                    )}

                    {course.status === 'rejected' && (
                      <button
                        onClick={() => handleSubmitForReview(course._id)}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
                      >
                        {t('courses.resubmitForReview')}
                      </button>
                    )}

                    {course.status === 'approved' && (
                      <button
                        onClick={() => handlePublish(course._id)}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                      >
                        {t('courses.publishCourse')}
                      </button>
                    )}

                    {course.status === 'published' && (
                      <Link
                        to={`/my-teaching/courses/${course._id}/students`}
                        className="px-4 py-2 btn-gold rounded-md"
                      >
                        {t('courses.manageMembers')}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModal && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal(null)}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          variant={confirmModal.variant}
          confirmText={confirmModal.confirmText}
        />
      )}
    </div>
  );
}
